import * as React from 'react'

import {
  CONTENT_WRAPPER_CLASS_NAME,
  VERSION_COMPARISON_DEFAULT_PAGE,
  VERSION_COMPARISON_ZOOM_DEFAULT,
} from '../constants'
import { useVersionComparisonStore } from '../store'
import { TSelectedVersion } from '../types'

/**
 * Resets all shadow root DOM transforms to initial state
 * Removes zoom transforms from content-wrapper elements
 */
const resetShadowRootTransforms = (
  compareVersions: Array<{ id: string }> | null
) => {
  if (!compareVersions || compareVersions.length === 0) return

  compareVersions.forEach((version) => {
    // Find the shadow root wrapper div by version ID
    const shadowHostElement = document.getElementById(version.id)
    if (!shadowHostElement) return

    // Get the shadow root from the host element
    const shadowRoot = shadowHostElement.shadowRoot
    if (!shadowRoot) return

    // Find the content-wrapper inside the shadow root
    const contentWrapper = shadowRoot.querySelector(
      `.${CONTENT_WRAPPER_CLASS_NAME}`
    ) as HTMLElement

    if (contentWrapper) {
      // Reset transform to initial state (no zoom)
      contentWrapper.style.transform = `scale(${VERSION_COMPARISON_ZOOM_DEFAULT})`
      contentWrapper.style.transformOrigin = 'top left'
    }
  })
}

/**
 * Resets scroll positions on all scroll containers to initial state
 */
const resetScrollPositions = (
  compareVersions: Array<{ id: string }> | null
) => {
  if (!compareVersions || compareVersions.length === 0) return

  compareVersions.forEach((version) => {
    // Find the scrollable container by version ID
    const scrollContainer = document.getElementById(version.id) as HTMLElement
    if (!scrollContainer) return

    // Reset scroll position to top-left
    scrollContainer.scrollLeft = 0
    scrollContainer.scrollTop = 0
  })
}

/**
 * Comprehensive reset function for all layout handlers, controls, and DOM state
 */
const resetAllLayoutState = (compareVersions: Array<{ id: string }> | null) => {
  // Reset shadow root DOM transforms
  resetShadowRootTransforms(compareVersions)

  // Reset scroll positions
  resetScrollPositions(compareVersions)
}

interface UseLayoutResetOptions {
  /**
   * Current page state setter
   */
  setCurrentPage?: React.Dispatch<React.SetStateAction<number>>
  /**
   * Ref to the scroll container element
   */
  scrollContainerRef: React.RefObject<HTMLDivElement | null>
  /**
   * Ref to the shadow root element
   */
  shadowRootRef?: React.MutableRefObject<ShadowRoot | null>
}

/**
 * Hook to handle resetting all layout state when the comparison dialog closes
 *
 * This hook automatically detects when the dialog closes and resets:
 * - Current page to default
 * - Zoom scale to default
 * - Shadow root DOM transforms
 * - Scroll positions on all containers
 * - Local refs
 *
 * @param options - Configuration options for the reset hook
 */
export const useLayoutReset = ({
  setCurrentPage,
  scrollContainerRef,
  shadowRootRef,
}: UseLayoutResetOptions) => {
  const {
    documentId,
    compareVersions,
    setZoomScale,
    setCurrentPage: setGlobalCurrentPage,
  } = useVersionComparisonStore()

  // Store previous compareVersions to reset DOM when dialog closes
  const previousCompareVersionsRef = React.useRef<TSelectedVersion[] | null>(
    compareVersions
  )

  React.useEffect(() => {
    previousCompareVersionsRef.current = compareVersions
  }, [compareVersions])

  // Reset layout handlers when dialog closes (documentId or compareVersions become null/empty)
  React.useEffect(() => {
    const isDialogOpen =
      !!documentId && !!compareVersions && compareVersions.length > 0

    if (!isDialogOpen) {
      // Capture versions before they're cleared (for DOM reset)
      const versionsToReset = previousCompareVersionsRef.current

      // Reset current page to default when dialog closes
      setCurrentPage?.(VERSION_COMPARISON_DEFAULT_PAGE)
      setGlobalCurrentPage(VERSION_COMPARISON_DEFAULT_PAGE)

      // Reset zoom scale in store (this will trigger zoom reset in useZoomControl)
      setZoomScale(VERSION_COMPARISON_ZOOM_DEFAULT)

      // Reset all controls, configs, and shadow root DOM to initial state
      // Use a small timeout to ensure DOM elements are still accessible during cleanup
      const timeoutId = setTimeout(() => {
        if (versionsToReset) {
          resetAllLayoutState(versionsToReset)
        }

        // Reset local scroll container
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft = 0
          scrollContainerRef.current.scrollTop = 0
        }

        // Reset local shadow root transform if available
        if (shadowRootRef?.current) {
          const contentWrapper = shadowRootRef.current.querySelector(
            `.${CONTENT_WRAPPER_CLASS_NAME}`
          ) as HTMLElement
          if (contentWrapper) {
            contentWrapper.style.transform = `scale(${VERSION_COMPARISON_ZOOM_DEFAULT})`
            contentWrapper.style.transformOrigin = 'top left'
          }
        }

        // Clear shadow root ref
        if (shadowRootRef?.current) shadowRootRef.current = null
      }, 0)

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [
    documentId,
    compareVersions,
    setZoomScale,
    setGlobalCurrentPage,
    setCurrentPage,
    scrollContainerRef,
    shadowRootRef,
  ])
}
